import { useState, useEffect } from 'react';

export default function EmailCapturePopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Check if user has already seen the popup
    const hasSeenPopup = localStorage.getItem('emailCaptureShown');
    
    if (!hasSeenPopup) {
      // Show popup after 15 seconds
      const timer = setTimeout(() => {
        setIsVisible(true);
        localStorage.setItem('emailCaptureShown', 'true');
      }, 15000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d4jstih3esvd3qfl8gh0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email: email,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          setIsVisible(false);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-fadeIn">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        {/* Icon */}
        <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal-100 to-blue-100 rounded-full mx-auto mb-6">
          <i className="ri-file-text-line text-3xl text-teal-600"></i>
        </div>

        {/* Content */}
        <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
          Want to See a CrowdRounds Report?
        </h3>
        <p className="text-gray-600 text-center mb-6">
          Enter your email to get a sample intelligence report preview.
        </p>

        {/* Form */}
        <form id="sample-report-email-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="Enter your email"
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-base"
          />

          <button
            type="submit"
            disabled={isSubmitting || submitStatus === 'success'}
            className="w-full bg-gradient-to-r from-teal-600 to-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-teal-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Sending...' : submitStatus === 'success' ? 'Sent! ✓' : 'Send Me the Sample'}
          </button>

          {submitStatus === 'error' && (
            <p className="text-red-600 text-sm text-center">
              Something went wrong. Please try again.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
